/**
 * Model Registry — fetches and caches the full model list from the configured provider.
 *
 * Used by the model picker for searchable selection.
 * Cached per-session with a 10-minute refresh interval, per models endpoint.
 */

const DEFAULT_MODELS_BASE_URL = 'https://home.hoglundit.se/v1'

export interface ProviderModel {
    id: string
    name: string
    description?: string
    pricing: {
        prompt: number   // $/token, NaN when unavailable
        completion: number
    }
    context_length?: number
    top_provider?: {
        max_completion_tokens?: number
    }
}

/** Quick-access popular models shown at the top of the picker */
export const PINNED_MODELS = [
    'google/gemini-2.5-flash',
    'google/gemini-2.5-pro-preview',
    'anthropic/claude-sonnet-4',
    'anthropic/claude-3.5-haiku',
    'openai/gpt-4o-mini',
    'openai/gpt-4o',
    'deepseek/deepseek-r1',
    'deepseek/deepseek-chat-v3-0324',
]

const modelsCache = new Map<string, ProviderModel[]>()
const modelsFetchPromise = new Map<string, Promise<ProviderModel[]>>()
const lastFetchTime = new Map<string, number>()
const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

export async function fetchProviderModels(baseUrl?: string, apiKey?: string): Promise<ProviderModel[]> {
    const modelsUrl = buildModelsUrl(baseUrl)
    const cacheKey = buildModelsCacheKey(modelsUrl, apiKey)
    const now = Date.now()
    const cached = modelsCache.get(cacheKey)
    const fetchedAt = lastFetchTime.get(cacheKey) ?? 0
    if (cached && now - fetchedAt < CACHE_TTL) return cached

    const pending = modelsFetchPromise.get(cacheKey)
    if (pending) return pending

    const request = (async () => {
        try {
            const res = await fetch(modelsUrl, {
                headers: buildModelsHeaders(apiKey),
            })
            if (!res.ok) throw new Error(`Models API: ${res.status}`)
            const json = await res.json() as {
                data: Array<{
                    id: string
                    name: string
                    description?: string
                    pricing?: { prompt?: string; completion?: string }
                    context_length?: number
                    top_provider?: { max_completion_tokens?: number }
                }>
            }
            const models: ProviderModel[] = json.data
                .map(m => ({
                    id: m.id,
                    name: m.name || m.id.split('/').pop() || m.id,
                    description: m.description,
                    pricing: {
                        prompt: m.pricing?.prompt !== undefined ? parseFloat(m.pricing.prompt) : Number.NaN,
                        completion: m.pricing?.completion !== undefined ? parseFloat(m.pricing.completion) : Number.NaN,
                    },
                    context_length: m.context_length,
                    top_provider: m.top_provider,
                }))
                .sort((a, b) => {
                    // Pinned models first, then alphabetical
                    const aPin = PINNED_MODELS.indexOf(a.id)
                    const bPin = PINNED_MODELS.indexOf(b.id)
                    if (aPin >= 0 && bPin >= 0) return aPin - bPin
                    if (aPin >= 0) return -1
                    if (bPin >= 0) return 1
                    return a.id.localeCompare(b.id)
                })

            modelsCache.set(cacheKey, models)
            lastFetchTime.set(cacheKey, now)
            return models
        } catch (err) {
            console.warn(`[ModelRegistry] Failed to fetch models from ${modelsUrl}:`, err)
            return modelsCache.get(cacheKey) ?? []
        } finally {
            modelsFetchPromise.delete(cacheKey)
        }
    })()

    modelsFetchPromise.set(cacheKey, request)
    return request
}

function buildModelsUrl(baseUrl?: string): string {
    const normalizedBaseUrl = (baseUrl || DEFAULT_MODELS_BASE_URL).trim().replace(/\/+$/, '')
    return `${normalizedBaseUrl}/models`
}

function buildModelsCacheKey(modelsUrl: string, apiKey?: string): string {
    return `${modelsUrl}|${apiKey?.trim() ? `auth:${apiKey.trim()}` : 'anon'}`
}

function buildModelsHeaders(apiKey?: string): HeadersInit | undefined {
    const trimmed = apiKey?.trim()
    if (!trimmed) return undefined
    return { Authorization: `Bearer ${trimmed}` }
}

/** Format price for display: $/1M tokens */
export function formatPrice(pricePerToken: number): string {
    if (!Number.isFinite(pricePerToken)) return 'n/a'
    const perMillion = pricePerToken * 1_000_000
    if (perMillion === 0) return 'free'
    if (perMillion < 0.01) return '<$0.01/M'
    if (perMillion < 1) return `$${perMillion.toFixed(2)}/M`
    return `$${perMillion.toFixed(1)}/M`
}

export { DEFAULT_MODELS_BASE_URL, buildModelsUrl }
